import { MenuOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { findNavGroup, findNavItem } from "../navigation";
import { UserMenu } from "./UserMenu";

interface TopbarProps {
    onOpenMenu: () => void;
    onOpenCommandPalette: () => void;
}

/** True on macOS, where the palette shortcut is ⌘K rather than Ctrl K. */
const isApple = () => /Mac|iPhone|iPad/.test(navigator.platform ?? "");

/**
 * The persistent bar: where am I, how do I get somewhere else, who am I.
 *
 * The trail names the group as well as the section, which is what makes the
 * grouped sidebar legible — "Care library › Activities" tells an admin the shape
 * of the app in a way a lone page title never did.
 */
export const Topbar = ({ onOpenMenu, onOpenCommandPalette }: TopbarProps) => {
    const { t } = useTranslation();
    const location = useLocation();

    const item = findNavItem(location.pathname);
    const group = findNavGroup(item);

    return (
        <header className="topbar">
            <button
                type="button"
                className="topbar__icon-button"
                onClick={onOpenMenu}
                aria-label={t("nav.menu")}
            >
                <MenuOutlined />
            </button>

            <div className="topbar__trail">
                {group != null ? (
                    <>
                        <span>{t(group.labelKey)}</span>
                        <RightOutlined className="topbar__trail-sep" />
                    </>
                ) : null}
                <span className="topbar__trail-current">
                    {item != null ? t(item.labelKey) : t("menu.account")}
                </span>
            </div>

            <div className="topbar__spacer" />

            <button
                type="button"
                className="cmd-trigger"
                onClick={onOpenCommandPalette}
                aria-label={t("command.open")}
            >
                <SearchOutlined />
                <span className="cmd-trigger__label">{t("command.open")}</span>
                <span className="kbd">{isApple() ? "⌘K" : "Ctrl K"}</span>
            </button>

            <UserMenu />
        </header>
    );
};
